/** @jsxImportSource @emotion/react */
import { yupResolver } from '@hookform/resolvers/yup';
import { get } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { AppToken } from '../../../App';
import { Spinner, notify } from '../../../components';
import { COLORS } from '../../../styles/colors';
import { createFolder } from '../../../utils/apiHelper';
import { createFolderSchema } from '../../../utils/validation';
import CommonModal from '../CommonModal';
import ErrorMessageWrapper from '../ErrorMessageWrapper';
import FormTextBox from '../FormTextBox';

interface ICreateFolderModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  folderId: number | null;
  folderName?: string | '';
  setIsInit: (inIt: boolean) => void;
}

export interface ICreateFolderForm extends FieldValues {
  folderName: string;
}

const CreateFolderModal = ({
  isOpen,
  setIsOpen,
  folderId,
  folderName,
  setIsInit,
}: ICreateFolderModalProps) => {
  const navigate = useNavigate();

  const [folderStage, setFolderStage] = useState('');
  const [isShowSpinner, setIsShowSpinner] = useState(false);
  const { authorizedToken } = JSON.parse(useContext(AppToken));
  // const { handleSubmit, control } = useForm<ICreateFolderForm>({
  //   defaultValues: {
  //     folderName: '',
  //   },
  // });

  const defaultValues = {
    folderName: '',
  };

  const {
    register,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<ICreateFolderForm>({
    defaultValues: defaultValues,
    mode: 'onBlur',
    resolver: yupResolver(createFolderSchema),
  });

  const onSubmit: SubmitHandler<ICreateFolderForm> = async (data) => {
    setIsShowSpinner(true);
    const { errorMessage, response } = await createFolder(
      {
        folderId: folderId && folderId > 0 ? folderId : null,
        folderName: data.folderName,
      },
      authorizedToken,
    );
    if (response) {
      setIsOpen(false);
      setIsShowSpinner(false);
      notify({
        message: response.message || 'Folder created successfully',
        type: 'success',
      });
    }

    if (errorMessage) {
      setIsShowSpinner(false);
      notify({
        message: errorMessage,
        type: 'error',
      });
    }
    setIsInit(true);
    navigate('/', { replace: true });
  };

  useEffect(() => {
    if (!folderName) {
      setFolderStage('ADD');
      reset({
        folderName: '',
      });
    } else {
      setFolderStage('VIEW');
      reset({
        folderName,
      });
    }
  }, [folderName, isOpen]);

  const hasError = !!get(errors, 'folderName');

  return (
    <>
      {isShowSpinner ? (
        <Spinner
          commonStyles={{
            alignItems: 'center',
            flex: 1,
          }}
        />
      ) : null}

      <CommonModal
        isOpen={isOpen}
        title={`Create a folder under s:\\${folderName ? folderName : ''}?`}
        setIsOpen={setIsOpen}
        onSubmit={handleSubmit(onSubmit)}
        submitText="Create"
      >
        <div
          css={{
            marginTop: '1.5rem',
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          <div css={{ width: '100%' }}>
            <FormTextBox
              register={register}
              //id="addFolderTextBox"
              label="Enter your folder name"
              dataField="folderName"
              //control={control}
              css={{
                background: COLORS.white,
                border: hasError
                  ? `0.063rem solid ${COLORS.red3}`
                  : `0.063rem solid ${COLORS.grey18}`,
                borderRadius: '0.25rem',
                padding: '0.625rem',
                marginTop: '1.25rem',
                height: '12.5rem',
                resize: 'none',
                ':focus-visible': {
                  outline: 'none',
                },
                ...(folderStage === 'VIEW' && {
                  background: COLORS.white4,
                }),
                ...(hasError && {
                  border: `0.063rem solid ${COLORS.red3}`,
                }),
              }}
            />
            {errors && (
              <ErrorMessageWrapper errors={errors} dataField="folderName" />
            )}
          </div>
        </div>
      </CommonModal>
    </>
  );
};

export default CreateFolderModal;
